// The rebinding model (decision 0063): the player's binding overrides and hold-or-toggle choices over the game's
// contract, validated against it and kept in a storage the game passes in (localStorage in a browser), so they
// survive a reload. A rebinding screen is a view over this model:
//
//   list()                         every action with its bindings (current and default controls, rebindable or not)
//   startCapture(id, controls, o)  the next press of the binding's family becomes its control (Escape cancels)
//   set(id, controls, o)           set a binding's controls; `setPart` sets one key of a four-key dpad
//   reset(id?)                     back to the default (one binding, or all)
//   conflicts()                    controls two actions of one context share
//   setHoldMode(action, mode)      hold or toggle, for actions that allow it
//   onChange(fn)                   hear every change (with the effective contract)
//
// Each change is checked: a control must exist on the binding's family and fit its shape, the contract must still
// pass `checkContract`, and a new conflict is refused (the default), allowed, or swapped (the other action takes the
// old control). Touch bindings are not rebindable here: the touch layout (data) is how touch changes.
import type { HoldMode } from "./actions";
import { checkContract, conflicts, controlInfo, familyOf, shapeOf, type BindingDecl, type BindingShape, type CheckOptions, type Conflict, type ContractIssue, type Device, type DeviceFamily, type InputContract, type ValueType } from "./contract";
import type { Controls } from "./controls";

/** The part of `Storage` the model uses (localStorage fits; tests pass a Map-backed object). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The stored format's tag. */
export const INPUT_PREFS_FORMAT = "rpg.input-prefs@1" as const;
/** The default storage key. */
export const INPUT_PREFS_KEY = "rpg.input-prefs";

/** What is stored: binding overrides by binding id, and hold modes by action. JSON. */
export interface InputPrefs {
  format: typeof INPUT_PREFS_FORMAT;
  bindings: Record<string, string[]>;
  holdModes: Record<string, HoldMode>;
}

/** One binding as a rebinding screen shows it. */
export interface BindingInfo {
  id: string;
  action: string;
  device: Device;
  family: DeviceFamily;
  shape: BindingShape;
  controls: string[];
  defaults: string[];
  overridden: boolean;
  /** Buttons and dpads on keyboard, mouse and pad are; touch, vectors and pointer motion aren't. */
  rebindable: boolean;
}

/** One action as a rebinding screen shows it. */
export interface ActionInfo {
  id: string;
  type: ValueType;
  context: string;
  holdOrToggle: boolean;
  holdMode: HoldMode;
  /** The action whose controls this one shares (no bindings of its own; it follows that action's). */
  shares?: string;
  bindings: BindingInfo[];
}

/** What to do when a change would bind a control another action of the same context already has. */
export type ConflictPolicy = "reject" | "allow" | "swap";

/** The outcome of a change. */
export interface SetResult {
  ok: boolean;
  /** Why it was refused. */
  reason?: string;
  /** The contract check's errors the change would have caused. */
  issues: ContractIssue[];
  /** New conflicts (refused under "reject", kept under "allow", resolved under "swap"). */
  conflicts: Conflict[];
  /** Bindings changed to resolve a swap. */
  swapped: string[];
}

/** A running capture (see `Rebinding.startCapture`). */
export interface Capture {
  readonly binding: string;
  readonly part: number | undefined;
  cancel(): void;
}

export interface RebindingOptions {
  /** The game's contract: the defaults. */
  contract: InputContract;
  /** Where overrides live; null keeps them in memory only. */
  storage?: StorageLike | null;
  key?: string;
  /** The families the contract must keep covering (as `checkContract`). */
  check?: CheckOptions;
  /** The game's default hold modes (its settings table), for actions with `holdOrToggle`. */
  holdModes?: Readonly<Record<string, HoldMode>>;
}

const REBINDABLE: readonly BindingShape[] = ["button", "dpad"];

export class Rebinding {
  /** Problems met loading the stored prefs (a bad override is dropped, not applied). */
  readonly loadIssues: string[] = [];
  private readonly base: InputContract;
  private readonly storage: StorageLike | null;
  private readonly key: string;
  private readonly checkOpts: CheckOptions;
  private readonly defaultModes: Readonly<Record<string, HoldMode>>;
  private overrides = new Map<string, string[]>();
  private modes = new Map<string, HoldMode>();
  private fns = new Set<(effective: InputContract) => void>();
  private capture: { binding: string; part: number | undefined; stop: () => void } | null = null;
  private eff: InputContract;

  constructor(o: RebindingOptions) {
    this.base = o.contract;
    this.storage = o.storage ?? null;
    this.key = o.key ?? INPUT_PREFS_KEY;
    this.checkOpts = o.check ?? {};
    this.defaultModes = o.holdModes ?? {};
    this.eff = this.base;
    this.load();
  }

  /** The contract with the overrides applied. */
  effective(): InputContract {
    return this.eff;
  }

  /** Every action with its bindings, in contract order. */
  list(): ActionInfo[] {
    return this.eff.actions.map((a) => ({
      id: a.id,
      type: a.type,
      context: a.context,
      holdOrToggle: a.holdOrToggle === true,
      holdMode: this.holdMode(a.id),
      ...(a.shares !== undefined ? { shares: a.shares } : {}),
      bindings: this.eff.bindings.filter((b) => b.action === a.id).map((b) => this.info(b)),
    }));
  }

  /** One binding, or null. */
  binding(id: string): BindingInfo | null {
    const b = this.eff.bindings.find((x) => x.id === id);
    return b ? this.info(b) : null;
  }

  /** Controls two actions of one context share, under the current overrides. */
  conflicts(): Conflict[] {
    return conflicts(this.eff);
  }

  /** The stored form of the current overrides and modes. */
  prefs(): InputPrefs {
    return { format: INPUT_PREFS_FORMAT, bindings: Object.fromEntries(this.overrides), holdModes: Object.fromEntries(this.modes) };
  }

  /** Replace every override and hold mode with `p` (a stored `prefs()`, e.g. synced from another device), checked as
   *  a load is: bad entries are dropped and reported in the returned list. Saved and heard like any change. */
  applyPrefs(p: unknown): string[] {
    this.overrides.clear();
    this.modes.clear();
    this.eff = this.base;
    const issues = this.readPrefs(p);
    this.commit();
    return issues;
  }

  /** Set a binding's controls (all of them; a dpad takes four: up, down, left, right). */
  set(id: string, controls: readonly string[], o: { conflicts?: ConflictPolicy } = {}): SetResult {
    const r = this.tryChange(id, controls, o.conflicts ?? "reject");
    if (r.ok) this.commit();
    return r;
  }

  /** Set one control of a binding (one key of a dpad, or one of a button's alternatives) by index. */
  setPart(id: string, part: number, control: string, o: { conflicts?: ConflictPolicy } = {}): SetResult {
    const b = this.eff.bindings.find((x) => x.id === id);
    if (!b) return fail(`no binding "${id}"`);
    if (!(part >= 0 && part < Math.max(b.controls.length, 1))) return fail(`binding "${id}" has no part ${part}`);
    const next = [...b.controls];
    next[part] = control;
    return this.set(id, next, o);
  }

  /** Back to the defaults: one binding, or every binding and hold mode when `id` is omitted. */
  reset(id?: string): void {
    if (id === undefined) {
      this.overrides.clear();
      this.modes.clear();
    } else this.overrides.delete(id);
    this.commit();
  }

  /** The action's hold mode: the player's choice, else the game's default, else "hold". */
  holdMode(action: string): HoldMode {
    return this.modes.get(action) ?? this.defaultModes[action] ?? "hold";
  }

  /** Choose hold or toggle for an action that allows it (`holdOrToggle`). */
  setHoldMode(action: string, mode: HoldMode): SetResult {
    const a = this.base.actions.find((x) => x.id === action);
    if (!a) return fail(`no action "${action}"`);
    if (!a.holdOrToggle) return fail(`action "${action}" has no hold-or-toggle choice`);
    if (mode !== "hold" && mode !== "toggle") return fail(`hold mode "${String(mode)}" is not hold or toggle`);
    if (mode === (this.defaultModes[action] ?? "hold")) this.modes.delete(action);
    else this.modes.set(action, mode);
    this.commit();
    return { ok: true, issues: [], conflicts: [], swapped: [] };
  }

  /** Bind the next press on the binding's family (to `part` of it, or as its only control). A press in `cancel`
   *  (default Escape) cancels; presses of other families are ignored. `onDone` gets the result, or null when
   *  cancelled. One capture at a time: starting another cancels the first. */
  startCapture(
    id: string,
    source: Pick<Controls, "onPress">,
    o: { part?: number; cancel?: readonly string[]; conflicts?: ConflictPolicy; onDone?: (r: SetResult | null) => void } = {},
  ): Capture {
    this.capture?.stop();
    const b = this.binding(id);
    if (!b || !b.rebindable) throw new Error(`binding "${id}" ${b ? "is not rebindable" : "does not exist"}`);
    const cancel = o.cancel ?? ["Escape"];
    let off: (() => void) | null = null;
    const stop = (): void => {
      off?.();
      off = null;
      if (this.capture?.stop === stop) this.capture = null;
    };
    off = source.onPress((control) => {
      if (cancel.includes(control)) {
        stop();
        o.onDone?.(null);
        return;
      }
      const info = controlInfo(control);
      if (!info || info.family !== b.family || info.kind !== "button") return;
      stop();
      const r = o.part === undefined ? this.set(id, [control], { conflicts: o.conflicts }) : this.setPart(id, o.part, control, { conflicts: o.conflicts });
      o.onDone?.(r);
    });
    this.capture = { binding: id, part: o.part, stop };
    return {
      binding: id,
      part: o.part,
      cancel: () => {
        if (this.capture?.stop !== stop) return;
        stop();
        o.onDone?.(null);
      },
    };
  }

  /** The capture running now, if any. */
  capturing(): { binding: string; part: number | undefined } | null {
    return this.capture ? { binding: this.capture.binding, part: this.capture.part } : null;
  }

  /** Hear every change, with the new effective contract. Returns the remover. */
  onChange(fn: (effective: InputContract) => void): () => void {
    this.fns.add(fn);
    return () => this.fns.delete(fn);
  }

  // ── internals ────────────────────────────────────────────────────────────────────────────────────

  private info(b: BindingDecl): BindingInfo {
    const a = this.base.actions.find((x) => x.id === b.action);
    const shape = shapeOf(b, a?.type ?? "button");
    const def = this.base.bindings.find((x) => x.id === b.id);
    return {
      id: b.id,
      action: b.action,
      device: b.device,
      family: familyOf(b.device),
      shape,
      controls: [...b.controls],
      defaults: [...(def?.controls ?? [])],
      overridden: this.overrides.has(b.id),
      rebindable: b.device !== "touch" && REBINDABLE.includes(shape),
    };
  }

  /** Validate a change and, if it passes, stage it in `overrides` and `eff` (the caller commits). */
  private tryChange(id: string, controls: readonly string[], policy: ConflictPolicy): SetResult {
    const b = this.eff.bindings.find((x) => x.id === id);
    if (!b) return fail(`no binding "${id}"`);
    const info = this.info(b);
    if (!info.rebindable) return fail(`binding "${id}" is not rebindable`);
    const next = [...controls];
    if (info.shape === "dpad" ? next.length !== 4 : next.length === 0) return fail(info.shape === "dpad" ? "a dpad takes four controls" : "a binding needs a control");
    for (const c of next) {
      const ci = controlInfo(c);
      if (!ci) return fail(`no device writes "${c}"`);
      if (ci.family !== info.family) return fail(`"${c}" is a ${ci.family} control; binding "${id}" is ${info.family}`);
      if (ci.kind !== "button") return fail(`"${c}" is not a button`);
    }
    const device = controlInfo(next[0]!)!.device;
    let candidate = withControls(this.eff, id, next, device);
    const before = new Set(conflicts(this.eff).map(conflictKey));
    let fresh = conflicts(candidate).filter((k) => !before.has(conflictKey(k)) && k.bindings.includes(id));
    const swapped: string[] = [];
    if (fresh.length && policy === "reject") return { ok: false, reason: `conflicts with ${fresh.map((k) => k.actions.filter((x) => x !== b.action).join(", ")).join("; ")}`, issues: [], conflicts: fresh, swapped };
    if (fresh.length && policy === "swap") {
      // each other binding that now shares a control takes the control this binding gave up at the same index
      for (const k of fresh) {
        const i = next.indexOf(k.control);
        const old = b.controls[i] ?? b.controls[0] ?? "";
        for (const otherId of k.bindings) {
          if (otherId === id) continue;
          const other = candidate.bindings.find((x) => x.id === otherId)!;
          const oc = other.controls.map((c) => (c === k.control ? old : c));
          candidate = withControls(candidate, otherId, oc, other.device);
          swapped.push(otherId);
        }
      }
      fresh = conflicts(candidate).filter((k) => !before.has(conflictKey(k)));
    }
    const errors = checkContract(candidate, this.checkOpts).issues.filter((i) => i.severity === "error" && !(i.kind === "conflict" && policy === "allow"));
    const beforeErrors = new Set(checkContract(this.eff, this.checkOpts).issues.filter((i) => i.severity === "error").map((i) => i.message));
    const added = errors.filter((i) => !beforeErrors.has(i.message));
    if (added.length) return { ok: false, reason: added[0]!.message, issues: added, conflicts: fresh, swapped: [] };
    for (const x of [id, ...swapped]) {
      const now = candidate.bindings.find((y) => y.id === x)!.controls;
      const def = this.base.bindings.find((y) => y.id === x)!.controls;
      if (sameList(now, def)) this.overrides.delete(x);
      else this.overrides.set(x, [...now]);
    }
    this.eff = candidate;
    return { ok: true, issues: [], conflicts: fresh, swapped };
  }

  /** Rebuild the effective contract from the overrides, save, and tell listeners. */
  private commit(): void {
    this.eff = this.build();
    this.save();
    for (const f of this.fns) f(this.eff);
  }

  private build(): InputContract {
    let c = this.base;
    for (const [id, controls] of this.overrides) {
      const first = controls.find((x) => x !== "");
      c = withControls(c, id, controls, first ? controlInfo(first)?.device : undefined);
    }
    return c;
  }

  private save(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key, JSON.stringify(this.prefs()));
    } catch (e) {
      this.loadIssues.push(`could not store input prefs: ${String(e)}`);
    }
  }

  private load(): void {
    if (!this.storage) return;
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.key);
    } catch (e) {
      this.loadIssues.push(`could not read input prefs: ${String(e)}`);
      return;
    }
    if (raw === null) return;
    let p: unknown;
    try {
      p = JSON.parse(raw);
    } catch {
      this.loadIssues.push("stored input prefs are not JSON; ignored");
      return;
    }
    this.loadIssues.push(...this.readPrefs(p));
  }

  /** Apply stored prefs on top of the current state; the problems, one line each. */
  private readPrefs(raw: unknown): string[] {
    const issues: string[] = [];
    const p = raw as Partial<InputPrefs> | null;
    if (p?.format !== INPUT_PREFS_FORMAT) return [`stored input prefs have format ${String(p?.format)}, not ${INPUT_PREFS_FORMAT}; ignored`];
    for (const [id, controls] of Object.entries(p.bindings ?? {})) {
      const r = Array.isArray(controls) && controls.every((c) => typeof c === "string") ? this.tryChange(id, controls, "allow") : fail("not a list of controls");
      if (!r.ok) issues.push(`stored binding "${id}" dropped: ${r.reason}`);
    }
    for (const [action, mode] of Object.entries(p.holdModes ?? {})) {
      const a = this.base.actions.find((x) => x.id === action);
      if (!a?.holdOrToggle || (mode !== "hold" && mode !== "toggle")) issues.push(`stored hold mode for "${action}" dropped`);
      else if (mode !== (this.defaultModes[action] ?? "hold")) this.modes.set(action, mode);
    }
    this.eff = this.build();
    return issues;
  }
}

function fail(reason: string): SetResult {
  return { ok: false, reason, issues: [], conflicts: [], swapped: [] };
}

function withControls(c: InputContract, id: string, controls: readonly string[], device: Device | undefined): InputContract {
  return { ...c, bindings: c.bindings.map((b) => (b.id === id ? { ...b, controls: [...controls], device: device && familyOf(device) === familyOf(b.device) ? device : b.device } : b)) };
}

const conflictKey = (k: Conflict): string => `${k.context}\u0000${k.control}\u0000${[...k.actions].sort().join(",")}`;
const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);
